/**
 * `ConnectedAppCallLog` — the per-connection call log, opened from the
 * Log button on a Connected apps row (founder request 2026-11-01).
 *
 * One modal: a search box, a sortable table (When · Tool · Site ·
 * Outcome · Took) and a pager, all served by
 * `GET /v1/oauth/grants/:grant_id/calls`. Search, sort and paging are
 * server-side so the view stays honest against the full 90-day log,
 * not just the rows it happened to fetch.
 *
 * What it can never show: arguments or results. The analytics rows
 * record the tool, the site it named, how the call ended and how long
 * it took, nothing else, so the log reads "block_sender · shop.example
 * · ok" and never what was blocked. The subtitle says so.
 */

import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ConnectedAppCall, ConnectedAppCallSort, ConnectedAppCallsResponse, ConnectedAppGrant } from '@sill/types/api';

import { listConnectedAppCalls } from '../../api/connected-apps.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { Button, Modal, Pill, type PillTone } from '../../components/atoms/index.js';
import { formatUtcDateTime } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { useIsMobile } from '../../lib/use-is-mobile.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** Keystrokes settle for this long before a search request goes out. */
export const CALL_LOG_SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 25;

type SortDir = 'asc' | 'desc';

const COLUMNS: ReadonlyArray<{ readonly key: ConnectedAppCallSort; readonly label: string }> = [
  { key: 'at', label: 'When' },
  { key: 'tool', label: 'Tool' },
  { key: 'site', label: 'Site' },
  { key: 'outcome', label: 'Outcome' },
  { key: 'duration', label: 'Took' },
];

/** Newest first for time; A→Z / smallest first for everything else. */
export function defaultDirFor(key: ConnectedAppCallSort): SortDir {
  return key === 'at' ? 'desc' : 'asc';
}

/**
 * Outcome → pill tone + text. `tool_error` names the error code the tool
 * returned (`confirmation_required`, `not_found`, …) because that is the
 * part a person can act on, and stays as the identifier; every other
 * outcome reads as an uppercase word like the rest of the Pill atom.
 */
export function describeOutcome(call: Pick<ConnectedAppCall, 'outcome' | 'error_type'>): { tone: PillTone; text: string } {
  switch (call.outcome) {
    case 'ok':
      return { tone: 'lime', text: 'OK' };
    case 'tool_error':
      return { tone: 'amber', text: call.error_type ?? 'ERROR' };
    case 'rate_limited':
      return { tone: 'amber', text: 'RATE LIMITED' };
    case 'forbidden':
    case 'unauthorized':
      return { tone: 'coral', text: call.outcome.toUpperCase() };
    case 'inner_failure':
      return { tone: 'coral', text: 'FAILED' };
    case 'invalid_params':
      return { tone: 'neutral', text: 'INVALID INPUT' };
    case 'unknown_tool':
      return { tone: 'neutral', text: 'UNKNOWN TOOL' };
    default:
      return { tone: 'neutral', text: call.outcome.toUpperCase() };
  }
}

export function formatTook(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

type LogState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly response: ConnectedAppCallsResponse }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export function ConnectedAppCallLog({
  grant,
  onClose,
}: {
  readonly grant: ConnectedAppGrant;
  readonly onClose: () => void;
}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  // Below 640px the five-column table cannot fit; rows stack and the
  // sort moves into a select so nothing scrolls sideways inside the modal.
  const mobile = useIsMobile(639);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState<ConnectedAppCallSort>('at');
  const [dir, setDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [state, setState] = useState<LogState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), CALL_LOG_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    setState((s) => (s.kind === 'ready' ? s : { kind: 'loading' }));
    void listConnectedAppCalls(http, grant.grant_id, { page, page_size: PAGE_SIZE, q: debounced, sort, dir }).then((r) => {
      if (cancelled) return;
      if (r.kind === 'ok') setState({ kind: 'ready', response: r.response });
      else if (r.kind === 'not_found') setState({ kind: 'not_found' });
      else setState({ kind: 'error', message: r.message });
    });
    return () => {
      cancelled = true;
    };
  }, [http, grant.grant_id, page, debounced, sort, dir, reloadKey]);

  const toggleSort = useCallback(
    (key: ConnectedAppCallSort) => {
      if (key === sort) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setSort(key);
        setDir(defaultDirFor(key));
      }
      setPage(1);
    },
    [sort],
  );

  const response = state.kind === 'ready' ? state.response : null;
  const total_pages = response?.total_pages ?? 1;
  const count_line = useMemo(() => {
    if (response === null) return '';
    const n = response.total.toLocaleString('en-US');
    return debounced.length > 0 ? `${n} matching` : `${n} calls · 90 days`;
  }, [response, debounced]);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Call log · ${grant.client_label}`}
      subtitle="Tool calls made through this connection. Tool, site, outcome and time only; arguments and results are never stored."
    >
      <div className="px-6 py-4 flex flex-col gap-3" data-testid="call-log">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              background: C.surface,
              padding: '6px 10px',
              minWidth: 220,
            }}
          >
            <Search size={12} color={C.muted} />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search tool, site or outcome"
              aria-label="Search calls"
              style={{ background: 'transparent', border: 'none', outline: 'none', fontFamily: sansFont, fontSize: 13, color: C.text, width: '100%' }}
            />
          </label>
          <div data-testid="call-log-count" style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}>
            {count_line}
          </div>
        </div>

        {state.kind === 'not_found' ? (
          <Note C={C}>This connection is no longer available.</Note>
        ) : state.kind === 'error' ? (
          <div className="flex items-center gap-3">
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{state.message}</span>
            <Button variant="ghost" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          </div>
        ) : mobile ? (
          <div className="flex flex-col" data-testid="call-log-stacked">
            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
              <label style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: C.muted }}>
                Sort
                <select
                  aria-label="Sort by"
                  value={sort}
                  onChange={(e) => {
                    const key = e.target.value as ConnectedAppCallSort;
                    setSort(key);
                    setDir(defaultDirFor(key));
                    setPage(1);
                  }}
                  style={{ marginLeft: 8, fontFamily: sansFont, fontSize: 12, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2, padding: '4px 6px' }}
                >
                  {COLUMNS.map((col) => (
                    <option key={col.key} value={col.key}>{col.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => {
                  setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                  setPage(1);
                }}
                aria-label={dir === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
                style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, padding: '4px 6px', cursor: 'pointer', color: C.muted, display: 'inline-flex' }}
              >
                {dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              </button>
            </div>
            {response === null ? (
              <div style={{ padding: '14px 0', fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div>
            ) : response.calls.length === 0 ? (
              <div style={{ padding: '14px 0', fontFamily: sansFont, fontSize: 13, color: C.muted }} data-testid="call-log-empty">
                {debounced.length > 0 ? 'No calls match.' : 'No calls yet.'}
              </div>
            ) : (
              response.calls.map((call) => <StackedCall key={call.call_id} call={call} />)
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }} aria-label="Call log">
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      aria-sort={sort === col.key ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      style={{
                        textAlign: col.key === 'duration' ? 'right' : 'left',
                        padding: '6px 8px 6px 0',
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: 1.6,
                        textTransform: 'uppercase',
                        color: C.muted,
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          font: 'inherit',
                          color: sort === col.key ? C.text : 'inherit',
                          letterSpacing: 'inherit',
                          textTransform: 'inherit',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        {col.label}
                        {sort === col.key ? (dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {response === null ? (
                  <tr>
                    <td colSpan={COLUMNS.length} style={{ padding: '14px 0', fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                      Loading…
                    </td>
                  </tr>
                ) : response.calls.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} style={{ padding: '14px 0', fontFamily: sansFont, fontSize: 13, color: C.muted }} data-testid="call-log-empty">
                      {debounced.length > 0 ? 'No calls match.' : 'No calls yet.'}
                    </td>
                  </tr>
                ) : (
                  response.calls.map((call) => <CallRow key={call.call_id} call={call} />)
                )}
              </tbody>
            </table>
          </div>
        )}

        {response !== null && response.total > PAGE_SIZE ? (
          <div className="flex items-center justify-between gap-3" style={{ borderTop: `1px solid ${C.borderSoft}`, paddingTop: 10 }}>
            <span data-testid="call-log-range" style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}>
              {((response.page - 1) * PAGE_SIZE + 1).toLocaleString('en-US')}–{Math.min(response.page * PAGE_SIZE, response.total).toLocaleString('en-US')} of {response.total.toLocaleString('en-US')}
            </span>
            <div className="flex items-center gap-1">
              <PagerButton C={C} disabled={response.page <= 1} onClick={() => setPage(response.page - 1)} label="PREV" />
              <span data-testid="call-log-page" style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted, padding: '0 6px' }}>
                {response.page} / {total_pages}
              </span>
              <PagerButton C={C} disabled={response.page >= total_pages} onClick={() => setPage(response.page + 1)} label="NEXT" />
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function CallRow({ call }: { readonly call: ConnectedAppCall }): JSX.Element {
  const C = useC();
  const outcome = describeOutcome(call);
  const cell = { padding: '8px 8px 8px 0', borderBottom: `1px solid ${C.borderSoft}`, verticalAlign: 'middle' as const };
  return (
    <tr data-testid={`call-row-${call.call_id}`}>
      <td style={{ ...cell, fontFamily: monoFont, fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>{formatUtcDateTime(call.at)}</td>
      <td style={{ ...cell, fontFamily: monoFont, fontSize: 12, color: C.text, whiteSpace: 'nowrap' }}>
        {call.tool ?? '—'}
        {call.tool_kind === 'write' ? <span style={{ color: C.dim, marginLeft: 6, fontSize: 10 }}>write</span> : null}
      </td>
      <td style={{ ...cell, fontFamily: sansFont, fontSize: 12, color: call.site_domain === null ? C.dim : C.text }}>{call.site_domain ?? '—'}</td>
      <td style={cell}>
        <Pill tone={outcome.tone}>{outcome.text}</Pill>
      </td>
      <td style={{ ...cell, fontFamily: monoFont, fontSize: 11, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatTook(call.duration_ms)}</td>
    </tr>
  );
}

/** Phone layout: tool + outcome on the first line, time · site · took on the second. */
function StackedCall({ call }: { readonly call: ConnectedAppCall }): JSX.Element {
  const C = useC();
  const outcome = describeOutcome(call);
  return (
    <div data-testid={`call-row-${call.call_id}`} style={{ padding: '8px 0', borderBottom: `1px solid ${C.borderSoft}` }}>
      <div className="flex items-center justify-between gap-2">
        <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {call.tool ?? '—'}
          {call.tool_kind === 'write' ? <span style={{ color: C.dim, marginLeft: 6, fontSize: 10 }}>write</span> : null}
        </span>
        <Pill tone={outcome.tone}>{outcome.text}</Pill>
      </div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 4, overflowWrap: 'anywhere' }}>
        {formatUtcDateTime(call.at)}
        {call.site_domain !== null ? ` · ${call.site_domain}` : ''}
        {` · ${formatTook(call.duration_ms)}`}
      </div>
    </div>
  );
}

function Note({ C, children }: { readonly C: ReturnType<typeof useC>; readonly children: string }): JSX.Element {
  return <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, padding: '14px 0' }}>{children}</div>;
}

function PagerButton({
  C,
  disabled,
  onClick,
  label,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="px-3 py-1.5"
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1,
        color: disabled ? C.dim : C.text,
        background: 'transparent',
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}
